/**
 * Persistent outbound queue (IndexedDB). A message typed while offline, or whose send failed,
 * survives reloads and is retried with the same clientMsgId, so the server never stores it twice.
 * Falls back to memory when IndexedDB is unavailable (private mode): retries still work in-tab.
 */
import type { AttachmentDto } from '@dijitalapp/shared';

/** An encrypted upload waiting to be announced in an encrypted message (key and metadata stay on this device). */
export interface E2eeFileMeta {
  attachmentId: string;
  name: string;
  mime: string;
  size: number;
  /** Key, IV and hash from the crypto library (Matrix EncryptedFile, without the url). */
  info: Record<string, unknown>;
  voice: boolean;
  durationMs?: number;
}

export interface QueuedMessage {
  clientMsgId: string;
  conversationId: string;
  body: string;
  createdAt: string;
  /** Already uploaded; only the message referencing it is queued. */
  attachment?: AttachmentDto;
  replyToId?: string;
  linkPreviewUrl?: string;
  e2eeFile?: E2eeFileMeta;
  /** A copy forwarded from another chat (encrypted chats forward by decrypting and sending again). */
  forwarded?: boolean;
}

const DB_NAME = 'dijitalapp';
const STORE = 'outbox';
const memory = new Map<string, QueuedMessage>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  dbPromise ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'clientMsgId' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(undefined);
        const req = fn(db.transaction(STORE, mode).objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(undefined);
      }),
  );
}

export const outbox = {
  async put(m: QueuedMessage): Promise<void> {
    memory.set(m.clientMsgId, m);
    await run('readwrite', (s) => s.put(m));
  },
  async remove(clientMsgId: string): Promise<void> {
    memory.delete(clientMsgId);
    await run('readwrite', (s) => s.delete(clientMsgId));
  },
  async all(): Promise<QueuedMessage[]> {
    const stored = (await run<QueuedMessage[]>('readonly', (s) => s.getAll())) ?? [];
    const merged = new Map(stored.map((m) => [m.clientMsgId, m]));
    for (const m of memory.values()) merged.set(m.clientMsgId, m);
    return [...merged.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  },
};

/** RFC 9562 UUIDv7 (time-ordered) for client message ids. */
export function uuidv7(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const ts = BigInt(Date.now());
  for (let i = 0; i < 6; i++) bytes[i] = Number((ts >> BigInt(8 * (5 - i))) & 0xffn);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
